import { useEffect, useState } from "react";
import { ActionIcon, Button, Group, Indicator, Menu, ScrollArea, Stack, Text } from "@mantine/core";
import { IconBell } from "@tabler/icons-react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import {
  getNotifications, getNotificationUnreadCount, markAllNotificationsRead, markNotificationRead,
} from "../api";
import { useStore } from "../store";
import { relativeTime } from "../_Shared/time";

const POLL_MS = 60000;

// Header bell: persisted in-app notifications (v0). Unread count polled; the dropdown lists the
// recent ones and clicking one marks it read + navigates to its deep link.
export default function NotificationBell() {
  const { t, i18n } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const unread = useStore((s) => s.notifUnread);
  const setUnread = useStore((s) => s.setNotifUnread);
  const [opened, setOpened] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const refresh = () => getNotificationUnreadCount().then((r) => setUnread(r?.count ?? 0)).catch(() => {});
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [setUnread]);

  const onOpenChange = (o) => {
    setOpened(o);
    if (o) getNotifications(30).then((rows) => setItems(rows ?? [])).catch(() => setItems([]));
  };

  const count = unread ?? 0;

  const openNotification = (n) => {
    setOpened(false);
    if (!n.read) {
      markNotificationRead(n.id).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread(Math.max(0, count - 1));
    }
    if (n.deepLink) setLocation(n.deepLink);
  };

  const markAll = () => {
    markAllNotificationsRead().catch(() => {});
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <Menu width={360} position="bottom-end" opened={opened} onChange={onOpenChange} closeOnItemClick={false}>
      <Menu.Target>
        <Indicator color="red" size={16} offset={4} disabled={count === 0} label={count > 99 ? "99+" : count}>
          <ActionIcon variant="subtle" size="lg" aria-label={t("notifications_aria")}>
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Menu.Target>
      <Menu.Dropdown>
        <Group justify="space-between" px="sm" py={6} wrap="nowrap">
          <Text fw={600} size="sm">{t("notifications_title")}</Text>
          {items.some((n) => !n.read) && (
            <Button variant="subtle" size="compact-xs" onClick={markAll}>{t("notifications_mark_all_read")}</Button>
          )}
        </Group>
        <Menu.Divider />
        {items.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="md">{t("notifications_empty")}</Text>
        ) : (
          <ScrollArea.Autosize mah={380}>
            {items.map((n) => (
              <Menu.Item key={n.id} onClick={() => openNotification(n)}>
                <Stack gap={2}>
                  <Text size="sm" fw={n.read ? 400 : 600} lineClamp={1}>{n.title}</Text>
                  <Text size="xs" c="dimmed" lineClamp={2}>{n.body}</Text>
                  <Text size="xs" c="dimmed">{relativeTime(n.createdAt, i18n.language)}</Text>
                </Stack>
              </Menu.Item>
            ))}
          </ScrollArea.Autosize>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
